import { CustomersSearch } from "@/sections/customer/customers-search";
import ArrowUpOnSquareIcon from "@heroicons/react/24/solid/ArrowUpOnSquareIcon";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Container,
  Grid,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import copy from "copy-to-clipboard";
import { toast } from "sonner";

import Modal from "@/components/modal";
import {
  useDeleteMediaImageMutation,
  useGetMediaImagesQuery,
} from "@/app/features/mediaGallery/mediaGalleryApiSlice";

const PAGE_SIZE = 10;

const IMAGE_BASE_URL_ORIGIN =
  "https://thebeep-au.syd1.digitaloceanspaces.com/car-images";

/** Bust browser/CDN cache when the same filename is re-uploaded (URL path unchanged). */
function carImageSrc(imageName, cacheKey, cacheVersion) {
  const base = `${IMAGE_BASE_URL_ORIGIN}/${imageName}`;
  const version = [cacheKey, cacheVersion].filter((v) => v != null && v !== "").join("-");
  return version ? `${base}?v=${encodeURIComponent(version)}` : base;
}

const MediaLibraryPage = () => {
  const [search, setSearch] = useState("");
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);

  const loadMoreImages = () => {
    setPageLimit(pageLimit + PAGE_SIZE);
  };

  const { data, isLoading, isError, refetch, isFetching, dataUpdatedAt } =
    useGetMediaImagesQuery(
      {
        search: encodeURIComponent(search),
        page: 1,
        limit: pageLimit,
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  if (isError) {
    return <p>Something went wrong</p>;
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={{ md: 4 }}
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h4">Media Gallery</Typography>
            <Button
              variant="contained"
              startIcon={
                <SvgIcon fontSize="small">
                  <ArrowUpOnSquareIcon />
                </SvgIcon>
              }
              component={Link}
              to="/media-gallery/upload-media"
            >
              Add New Media Images
            </Button>
          </Stack>
          {isLoading ? (
            <Grid spacing={2} mt={2} container>
              {Array(30)
                .fill("")
                .map((file, index) => (
                  <Grid item xs={12} sm={4} md={3} lg={2} key={index}>
                    <Skeleton
                      sx={{
                        transform: "scale(1, 1.0)",
                        transformOrigin: "0% 0%",
                      }}
                      width="100%"
                      height="100px"
                    />
                  </Grid>
                ))}
            </Grid>
          ) : (
            <>
              <CustomersSearch
                search={search}
                setSearch={setSearch}
                placeholder="Search by Make, Model or Grade"
              />
              <PreviewFiles
                images={data?.data?.carImageList}
                refetch={refetch}
                loadMoreImages={loadMoreImages}
                canLoadMoreImages={!(pageLimit > data?.data?.total)}
                imagesLoading={isLoading || isFetching}
                data={data?.data}
                cacheVersion={dataUpdatedAt}
              />
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default MediaLibraryPage;

function PreviewFiles({
  images: files,
  refetch,
  loadMoreImages,
  canLoadMoreImages,
  imagesLoading,
  data,
  cacheVersion,
}) {
  const [photoIndex, setPhotoIndex] = useState(0);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handlePopoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDeletePopoverClose = () => {
    setAnchorEl(null);
  };

  const isDeletePopoverOpen = Boolean(anchorEl);
  const id = isDeletePopoverOpen ? "image-delete-popover" : undefined;

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [deleteMediaImage, { isLoading }] = useDeleteMediaImageMutation();

  const handleImageDelete = async (id) => {
    try {
      await deleteMediaImage({ id });
      setAnchorEl(null);
      toast.success("Image deleted");
      handleClose();

      refetch();
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  if (files.length === 0) {
    return (
      <Stack
        sx={{
          color: "rgb(189, 189, 189)",
          backgroundColor: "rgb(250, 250, 250)",
          height: 300,
          width: "100%",
          m: 3,
        }}
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="subtitle1">No files</Typography>
      </Stack>
    );
  }

  return (
    <>
      <Grid spacing={2} mt={2} container>
        {files.map((file, index) => (
          <Grid
            item
            xs={12}
            sm={4}
            md={3}
            lg={2}
            sx={{
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
              borderRadius: "0.375rem",
              overflow: "auto",
              padding: "1rem",
              transition: "all 0.2s",
              width: "100%",
              marginTop: "0",
              marginBottom: "0",
              backgroundColor: "transparent",
              position: "relative",
            }}
            key={index}
          >
            <div
              onClick={() => {
                setPhotoIndex(index);
                handleOpen();
              }}
              key={index}
              style={{
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                borderRadius: "0.375rem", // 6px
                overflow: "auto",
                padding: "1rem", // 16px
                transition: "all 200ms",
                width: "100%",
                marginTop: 0,
                marginBottom: 0,
                backgroundColor: "transparent",
                position: "relative",
                cursor: "pointer",
              }}
            >
              <ImageView
                img={file.imageName}
                alt={file.imageName}
                cacheKey={file.id ?? file._id}
                cacheVersion={cacheVersion}
                refetch={refetch}
              />
            </div>
          </Grid>
        ))}

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{
            width: "fit-content",
          }}
        >
          <Card
            sx={{
              boxShadow: "none !important",
              width: 350,
            }}
          >
            <CardActionArea as={"div"} sx={{ mt: 4 }}>
              <CardMedia
                component="img"
                height="140"
                image={carImageSrc(
                  files[photoIndex]?.imageName,
                  files[photoIndex]?.id ?? files[photoIndex]?._id,
                  cacheVersion
                )}
                alt={files[photoIndex]?.imageName}
                sx={{
                  objectFit: "contain",
                }}
              />
              <CardContent sx={{ p: 1, mt: 1 }}>
                <Typography gutterBottom variant="p">
                  {files[photoIndex]?.imageName}
                </Typography>
              </CardContent>
            </CardActionArea>
            <CardActions disableSpacing>
              <IconButton
                type="button"
                disabled={isLoading}
                onClick={() => {
                  copy(
                    `https://thebeep-au.syd1.cdn.digitaloceanspaces.com/car-images/${files[photoIndex]?.imageName}`
                  );
                  toast.info("Copied to clipboard");
                }}
              >
                <ContentCopyIcon />
              </IconButton>

              <IconButton
                type="button"
                disabled={isLoading}
                aria-describedby={id}
                variant="contained"
                onClick={handlePopoverClick}
              >
                <DeleteIcon />
              </IconButton>

              <Popover
                id={id}
                open={isDeletePopoverOpen}
                anchorEl={anchorEl}
                onClose={handleDeletePopoverClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <Stack p={2}>
                  <p>Delete {files[photoIndex]?.imageName}?</p>

                  <Button
                    variant="contained"
                    disabled={isLoading}
                    onClick={() => {
                      handleImageDelete(files[photoIndex]?.id);
                    }}
                  >
                    {isLoading ? "Deleting" : "Delete"}
                  </Button>
                </Stack>
              </Popover>
            </CardActions>
          </Card>
        </Modal>
      </Grid>

      <Stack
        direction="column"
        gap={2}
        alignItems="center"
        justifyContent={"center"}
      >
        <Typography>
          Showing {data.carImageList.length} of {data.total} images
        </Typography>
        {canLoadMoreImages && (
          <Button
            type="button"
            onClick={loadMoreImages}
            variant="contained"
            disabled={imagesLoading}
            sx={{
              width: "max-content",
            }}
          >
            Load More
          </Button>
        )}
      </Stack>
    </>
  );
}

function ImageView({ img, alt, cacheKey, cacheVersion }) {
  const src = carImageSrc(img, cacheKey, cacheVersion);
  return (
    <>
      <img
        src={src}
        key={src}
        style={{
          objectFit: "cover",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          borderRadius: "0.375rem",
          width: "100%",
          maxHeight: "48rem",
        }}
        alt={alt}
      />
    </>
  );
}
